import { storeToRefs } from "pinia";
import { computed, h, type VNode } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationResolved, useRoute, useRouter } from "vue-router";

import AiIcon from "@/composition/shared/contentItem/AiIcon.vue";
import { useMessengerSocketStore } from "@/stores/messengerSocketStore.ts";
import { useUserStore } from "@/stores/user.ts";
import type { Project } from "@/types/globalTypes";

import useAuthorization from "./useAuthorization.ts";

export interface NavigationItem {
  name: Project;
  label: string;
  url: RouteLocationResolved;
  isPermitted: boolean;
  hasNewItems?: boolean;
  renderFn?: (props: { item: NavigationItem }) => VNode;
  mobileOnly?: boolean;
  additionalClickHandler?: () => void;
}

function useNavigation() {
  const { t } = useI18n();
  const userStore = useUserStore();
  const { userPermissions } = storeToRefs(userStore);
  const {
    isUserPermittedForAction,
    isPermittedForNewsfeed,
    isPermittedForCvd,
    isPermittedForAiResearch,
    isPermittedForDesk,
    isPermittedForMessenger,
    isPermittedForIntelligence,
  } = useAuthorization(userStore);
  const router = useRouter();
  const currentRoute = useRoute();
  const messengerSocketStore = useMessengerSocketStore();

  const permissionsLoaded = computed(() => !!userPermissions.value);

  const hasUnlimitedArchiveUsage = computed(() => {
    if (!permissionsLoaded.value) return false;
    return isUserPermittedForAction({
      type: "content",
      action: "search_unlimited",
    });
  });

  const getValueOfSlug = computed(() => {
    if (currentRoute.name === "cvdPage") {
      return currentRoute.params.slug;
    }
    if (userStore.preferences.preferredCvdPage) {
      return userStore.preferences.preferredCvdPage.split(",");
    } else {
      return "top-themen";
    }
  });

  const navigationItems = computed<NavigationItem[]>(() => {
    if (!permissionsLoaded.value) return [];

    const allItems: NavigationItem[] = [
      {
        name: "desk",
        isPermitted: isPermittedForDesk(),
        label: t("navigation.desk"),
        url: router.resolve({
          name: currentRoute.name === "DeskDetail" ? "DeskDetail" : "desk",
          params: {
            ...(currentRoute.name === "DeskDetail"
              ? {
                  id: currentRoute.params.id,
                }
              : {}),
          },
        }),
      },
      {
        name: "cvd",
        isPermitted: isPermittedForCvd(),
        label: t("navigation.cvd"),
        url: router.resolve({
          name: "cvdPage",
          params: {
            slug: getValueOfSlug.value,
          },
        }),
      },
      {
        name: "archive",
        isPermitted: isPermittedForNewsfeed(),
        label: hasUnlimitedArchiveUsage.value
          ? t("navigation.archiveUnlimited")
          : t("navigation.archiveLimited"),
        url: router.resolve({
          name: "feed",
        }),
      },
      {
        name: "messenger",
        isPermitted: isPermittedForMessenger(),
        additionalClickHandler: undefined, // the messenger won't need  an extra reset call here
        hasNewItems: messengerSocketStore.hasUpdatedData,
        label: t("navigation.messenger"),
        url: router.resolve({
          name: "messenger",
        }),
      },
      {
        name: "ai-research",
        isPermitted: isPermittedForAiResearch(),
        label: t("navigation.aiResearch"),
        url: router.resolve({
          name: "ai-research",
        }),
        renderFn: (props: any) => {
          return h(
            "div",
            {
              class: "flex gap-x-2.5 items-center",
            },
            [
              h(AiIcon, {
                class:
                  "text-neutral-emphasis group-hover:text-neutral-primary group-[.isActive]:text-neutral-inverse-primary",
              }),
              props.item.label,
              h(
                "span",
                {
                  class:
                    "inline-block text-[10px] border rounded-full px-1 py-0.5 border-neutral-faint text-neutral-subtle group-hover:border-neutral-soft hover:group-[.isActive]:border-neutral-inverse-primary group-[.isActive]:border-neutral-inverse-primary group-[.isActive]:text-neutral-inverse-primary",
                },
                ["BETA"],
              ),
            ],
          );
        },
      },
      {
        name: "intelligence",
        isPermitted: isPermittedForIntelligence(),
        label: t("navigation.intelligence"),
        url: router.resolve({
          name: "intelligence",
        }),
        renderFn: (props: any) => {
          return h(
            "div",
            {
              class: "flex gap-x-2.5 items-center",
            },
            [
              props.item.label,
              h(
                "span",
                {
                  class:
                    "inline-block text-[10px] border rounded-full px-1 py-0.5 border-neutral-faint text-neutral-subtle group-hover:border-neutral-soft hover:group-[.isActive]:border-neutral-inverse-primary group-[.isActive]:border-neutral-inverse-primary group-[.isActive]:text-neutral-inverse-primary",
                },
                ["BETA"],
              ),
            ],
          );
        },
      },
      {
        name: "archive",
        isPermitted: isPermittedForNewsfeed(),
        label: t("bookmarks.title"),
        url: router.resolve({
          name: "bookmark",
        }),
        mobileOnly: true,
      },
      {
        name: "archive",
        isPermitted: isPermittedForNewsfeed(),
        label: t("settings.title"),
        url: router.resolve({
          name: "settingsPage",
        }),
        mobileOnly: true,
      },
    ];
    return allItems.filter((item) => item.isPermitted);
  });

  const messengerNavigationItems = computed<NavigationItem[]>(() => {
    return [
      {
        name: "messenger",
        isPermitted: true,
        label: t("basic.enquiries"),
        url: router.resolve({
          name: "messages",
        }),
        additionalClickHandler: messengerSocketStore.resetUpdatedTopicData,
        hasNewItems: messengerSocketStore.isTopicSocketDataUpdated,
      },
      {
        name: "messenger",
        isPermitted: true,
        label: t("basic.comments"),
        url: router.resolve({
          name: "comments",
        }),
        additionalClickHandler: messengerSocketStore.resetUpdatedCommentData,
        hasNewItems: messengerSocketStore.isCommentSocketDataUpdated,
      },
    ];
  });

  const numberOfItems = computed(() => {
    return navigationItems.value.filter((item) => !item.mobileOnly).length;
  });

  return {
    navigationItems,
    messengerNavigationItems,
    numberOfNavigationItems: numberOfItems,
    hasUnlimitedArchiveUsage,
    permissionsLoaded,
  };
}

export default useNavigation;
